import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Login from './Login';

const Home = () => {
  const { user, loading, sessionChecked } = useAuth();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Reset state when user changes
  useEffect(() => {
    if (!user) {
      setUserRole(null);
      setProfileLoading(false);
    } else {
      setProfileLoading(true);
    }
  }, [user]);

  useEffect(() => {
    // Wait for session to be checked
    if (!sessionChecked || loading) return;

    // ✅ FIX 1: If not authenticated, stop loading and show Login component
    if (!user) {
      setProfileLoading(false);
      return; // Don't redirect - show Login component instead
    }

    // If authenticated, fetch profile and route to dashboard with auto-repair
    const fetchProfileAndRoute = async () => {
      try {
        const { data: profile, error } = await supabase
          ?.from('user_profiles')
          ?.select('role, status')
          ?.eq('id', user?.id)
          ?.single();

        if (error) {
          console.error('[Home] Error fetching profile:', error);
          
          // ✅ SESSION AUTO-REPAIR: If valid session exists but no profile, auto-create profile
          console.log('[Home] Auto-repairing session: creating profile for user', user?.id);
          
          const { error: upsertError } = await supabase
            ?.from('user_profiles')
            ?.upsert({
              id: user?.id,
              email: user?.email,
              full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0],
              role: user?.user_metadata?.role || 'customer'
            });

          if (upsertError) {
            console.error('[Home] Error creating profile during auto-repair:', upsertError);
            setProfileLoading(false);
            return;
          }

          console.log('[Home] Session auto-repair successful, fetching new profile');
          
          // Fetch the newly created profile
          const { data: newProfile } = await supabase
            ?.from('user_profiles')
            ?.select('role, status')
            ?.eq('id', user?.id)
            ?.single();

          setUserRole(newProfile?.role || 'customer');
          setUserStatus(newProfile?.status || 'active');
          setProfileLoading(false);
        } else {
          setUserRole(profile?.role);
          setUserStatus(profile?.status);
          
          // ✅ NEW: Fetch agent profile status if user is an agent
          if (profile?.role === 'agent') {
            const { data: agentProfile } = await supabase
              ?.from('agent_profiles')
              ?.select('status')
              ?.eq('user_id', user?.id)
              ?.single();
            
            setAgentStatus(agentProfile?.status || 'pending');
          }
          
          setProfileLoading(false);
        }
      } catch (err) {
        console.error('[Home] Unexpected error during profile fetch:', err);
        setProfileLoading(false);
      }
    };

    fetchProfileAndRoute();
  }, [user, loading, sessionChecked]);

  // ✅ UPDATED: Route based on role AND agent status
  useEffect(() => {
    if (!userRole) return;

    console.log('[Home] Routing user - role:', userRole, 'status:', userStatus, 'agentStatus:', agentStatus);

    switch (userRole) {
      case 'customer': navigate('/customer-dashboard', { replace: true });
        break;
      case 'agent':
        // ✅ NEW: Route agents based on their approval status
        if (agentStatus === 'approved') {
          navigate('/agent-dashboard', { replace: true });
        } else if (agentStatus === 'rejected') {
          navigate('/agent-rejection-notice', { replace: true });
        } else {
          navigate('/agent-vetting', { replace: true });
        }
        break;
      case 'staff': navigate('/staff-dashboard', { replace: true });
        break;
      default:
        navigate('/customer-dashboard', { replace: true });
    }
  }, [userRole, userStatus, agentStatus, navigate]);

  // Show loading state while auth is initializing
  if (loading || !sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ✅ FIX 1: Show Login component if not authenticated (instead of redirecting)
  if (!user && !profileLoading) {
    return <Login />;
  }

  // Show loading while fetching profile for authenticated users
  if (user && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // This should not render as navigation happens in useEffect
  return null;
};

export default Home;